import React, { useEffect, useMemo, useCallback, ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { InteractionStatus } from '../../../types/InteractionStatus';
import FormSubmitOrCancel from '../../molecules/Form/components/FormSubmitOrCancel/FormSubmitOrCancel';
import LiveRegion from '../../atoms/LiveRegion/LiveRegion';
import { FormResponseErrorType, FormUtils, useFormContext } from '../../molecules/Form';
import { translatePartOfStringWithLink } from '../../../lib/StringUtils/StringHTMLUtils';
import useRedirect from '../../../hooks/useRedirect';
import { useModalContext } from '../../organisms/ModalDialog/ModalContext';
import IUrl from '../../../types/IUrl';
import { StatusInfoError } from '../../../_lib/components/StatusInfo';
import { LinkingPageType } from '../../../types/api/LinkingPageType';
import { StatusInfoModifier } from '../../../_lib/components/StatusInfo/partials/StatusInfo';

export type YesNoFormTemplateProps = {
  headline: string | ReactElement;
  headlineId: string;
  // parentPageUrl is only used for team rooms
  parentPageUrl?: IUrl;
  transNamespaceAndKey: string;
  cancelAction: () => void;
  widgetId?: string;
  errorStatuses?: FormResponseErrorType[];
  hint?: string;
  linkingPages?: LinkingPageType;
};

export default function YesNoFormTemplate({
  cancelAction,
  hint,
  headline,
  headlineId,
  transNamespaceAndKey,
  parentPageUrl,
  widgetId,
  errorStatuses = [],
  linkingPages,
}: YesNoFormTemplateProps) {
  const { status, getStatus, responseUrl, response } = useFormContext();
  const { preventModalClose, closePreventedMessage } = useModalContext();
  const { t } = useTranslation();
  const [startRedirect] = useRedirect();

  const getUrlFromResponse = useCallback(() => {
    // parentPageUrl is available only for team rooms
    if (parentPageUrl && parentPageUrl.url) {
      return parentPageUrl.url;
    }

    // widgetId comes oly from TopHeadlineInteractions
    if (widgetId) {
      return `${responseUrl}#${widgetId}`;
    }

    return responseUrl;
  }, [responseUrl, parentPageUrl, widgetId]);

  const statusMessages = useMemo(() => {
    const messages = {
      [InteractionStatus.SUCCESS]: [
        FormUtils.createStatusMessage(
          translatePartOfStringWithLink(`${transNamespaceAndKey}.form.status.success`, getUrlFromResponse(), true)
        ),
        FormUtils.createStatusMessage(
          translatePartOfStringWithLink(
            `${transNamespaceAndKey}.form.status.successForward`,
            getUrlFromResponse(),
            false
          ),
          InteractionStatus.SENDING
        ),
      ],
      [InteractionStatus.SENDING]: FormUtils.createStatusMessage(t(`${transNamespaceAndKey}.form.status.sending`)),
      [InteractionStatus.FAIL]: FormUtils.createStatusMessage(t(`${transNamespaceAndKey}.form.status.fail`)),
      [FormResponseErrorType.PAGE_LOCKED]: FormUtils.createStatusMessage(
        translatePartOfStringWithLink('phrases:pageLocked', response?.data.pageLockedBy?.url, false, {
          name: response?.data.pageLockedBy?.name,
        })
      ),
    } as const;

    return errorStatuses.reduce(
      (a, error) => ({
        ...a,
        [error]: FormUtils.createStatusMessage(
          translatePartOfStringWithLink(`${transNamespaceAndKey}.status.${error}`, getUrlFromResponse(), false)
        ),
      }),
      messages
    );
  }, [
    transNamespaceAndKey,
    getUrlFromResponse,
    t,
    response?.data.pageLockedBy?.url,
    response?.data.pageLockedBy?.name,
    errorStatuses,
  ]);

  useEffect(() => {
    if (status === InteractionStatus.SUCCESS) {
      startRedirect(getUrlFromResponse());
    }
    if (status === InteractionStatus.FAIL) {
      preventModalClose(false);
    }
  }, [getUrlFromResponse, preventModalClose, startRedirect, status]);

  return (
    <>
      <FormSubmitOrCancel
        hint={hint}
        headline={headline}
        headlineId={headlineId}
        hierarchy={3}
        disableCancelButton={status === InteractionStatus.SENDING || status === InteractionStatus.SUCCESS}
        disableSubmitButton={status === InteractionStatus.SENDING || status === InteractionStatus.SUCCESS}
        cancelAction={cancelAction}
        acceptAction={() => preventModalClose(true)}
        linkingPages={linkingPages}
      />
      <LiveRegion htmlTag="div" invisible={false}>
        {getStatus(statusMessages)}
        {closePreventedMessage ? (
          <StatusInfoError message={closePreventedMessage} modifier={StatusInfoModifier.RIGHT} />
        ) : null}
      </LiveRegion>
    </>
  );
}
